import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@practium/ui';
import { DEFAULT_MAINTENANCE_FORM } from '../constants/maintenance.constants';
import {
	MaintenanceIdentificationSchema,
	MaintenanceSchema,
} from '../schemas/maintenance.schema';
import type { MaintenanceFormData } from '../types';
import {
	validateFixedParcellingFlowering,
	type MaintenanceParcellingContext,
} from '../utils/parcellingCycle';

export interface UseMaintenanceFormOptions {
	initialData?: MaintenanceFormData;
	onSubmit: (data: MaintenanceFormData) => void | Promise<void>;
	validationMode?: 'full' | 'identification';
}

export const useMaintenanceForm = ({
	initialData,
	onSubmit,
	validationMode = 'full',
}: UseMaintenanceFormOptions) => {
	const toast = useToast();
	const initialForm = initialData
		? { ...DEFAULT_MAINTENANCE_FORM, ...initialData }
		: { ...DEFAULT_MAINTENANCE_FORM };
	const [form, setForm] = useState<MaintenanceFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const parcellingContextRef = useRef<MaintenanceParcellingContext>({ floweringMonths: [] });
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (!initialData) return;
		const next = { ...DEFAULT_MAINTENANCE_FORM, ...initialData };
		setForm(next);
		initialFormRef.current = next;
		setErrors({});
	}, [initialData]);

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const set = useCallback(<K extends keyof MaintenanceFormData>(key: K, value: MaintenanceFormData[K]) => {
		setForm((current) => ({ ...current, [key]: value }));
		setErrors((current) => ({ ...current, [key as string]: '' }));
	}, []);

	const setParcellingContext = useCallback((context: MaintenanceParcellingContext) => {
		parcellingContextRef.current = context;
	}, []);

	const handleSubmit = async () => {
		const result =
			validationMode === 'identification'
				? MaintenanceIdentificationSchema.safeParse(form)
				: MaintenanceSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			return;
		}

		if (validationMode === 'full' && form.parcellingType === 'fixed') {
			const parcellingErrors = validateFixedParcellingFlowering(
				form.floweringMonth,
				parcellingContextRef.current.floweringMonths
			);
			if (parcellingErrors.floweringMonth) {
				setErrors(parcellingErrors);
				toast.error(parcellingErrors.floweringMonth);
				return;
			}
		}

		setErrors({});
		setIsLoading(true);
		try {
			await onSubmit(form);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Ocorreu um erro ao submeter formulário.');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, handleSubmit, isDirty, setParcellingContext };
};
